<template>
  <div class="project-calculation">
    <AccessBlock :id="PROJECT_CALCULATION_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-calculation-internal
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-calculator mr-2"></i>{{ $t('project_calculation') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-calculation-internal" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectCalculationInternal
                v-if="calculationInternalProject"
                :blockId="PROJECT_CALCULATION_BLOCK"
                :project="calculationInternalProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_FINANCIAL_MODEL_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-financial-model
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-file-excel-o mr-2"></i>{{ $t('project_financial_model') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-financial-model" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectFinancialModel
                v-if="financialModelProject"
                :project="financialModelProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_PLANT_DATA_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-plant-data
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-sun-o mr-2"></i>{{ $t('project_plant_data') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-plant-data" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectPlantData
                v-if="plantDataProject"
                :blockId="PROJECT_PLANT_DATA_BLOCK"
                :project="plantDataProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_FINANCING_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-financing
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-eur mr-2"></i>{{ $t('project_financing') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-financing" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectFinancing
                v-if="financingProject"
                :blockId="PROJECT_FINANCING_BLOCK"
                :project="financingProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_IAB_TAX_DATA_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-iab-tax-data
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-percent mr-2"></i>{{ $t('project_iab_tax_data') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-iab-tax-data" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectIabTaxData
                v-if="iabTaxDataProject"
                :blockId="PROJECT_IAB_TAX_DATA_BLOCK"
                :project="iabTaxDataProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>
  </div>
</template>

<script>
import AccessBlock from '@/components/Access/AccessBlock.vue';
import ProjectCalculationInternal from '@/blocks/project/ProjectCalculationInternal.vue';
import ProjectFinancialModel from '@/blocks/project/ProjectFinancialModel.vue';
import ProjectPlantData from '@/blocks/project/ProjectPlantData.vue';
import ProjectFinancing from '@/blocks/project/ProjectFinancing.vue';
import ProjectIabTaxData from '@/blocks/project/ProjectIabTaxData.vue';
import {
  PROJECT_CALCULATION_BLOCK,
  PROJECT_FINANCIAL_MODEL_BLOCK,
  PROJECT_PLANT_DATA_BLOCK,
  PROJECT_FINANCING_BLOCK,
  PROJECT_IAB_TAX_DATA_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`); it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Tab "Kalkulation" of the project page: the blocks "Kalkulation – intern", "Finanzmodell",
 * "Anlagedaten", "Finanzierung" and "IAB- und Steuerdaten" (plans
 * docs/work/_done/2026-10-project-block-calculation-internal,
 * docs/work/_done/2026-10-project-block-financial-model, docs/work/_done/2026-10-project-block-plant-data,
 * docs/work/_done/2026-10-project-block-financing and docs/work/_done/2026-10-project-block-iab-tax-data).
 */
export default {
  name: 'ProjectCalculation',
  components: {
    AccessBlock,
    ProjectCalculationInternal,
    ProjectFinancialModel,
    ProjectPlantData,
    ProjectFinancing,
    ProjectIabTaxData,
  },
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      PROJECT_CALCULATION_BLOCK,
      PROJECT_FINANCIAL_MODEL_BLOCK,
      PROJECT_PLANT_DATA_BLOCK,
      PROJECT_FINANCING_BLOCK,
      PROJECT_IAB_TAX_DATA_BLOCK,
    };
  },
  computed: {
    /**
     * Data of the block project-calculation: the excerpt from `Project.blocks`, null when not
     * loaded or denied. The unsaved project gets passed itself; the block shows only the hint to
     * save first.
     */
    calculationInternalProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.calculationInternal ?? null;
    },
    /**
     * Data of the block project-financial-model, null when not loaded or denied. The unsaved
     * project gets passed itself; the block shows only the hint to save first.
     */
    financialModelProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.financialModel ?? null;
    },
    /**
     * Data of the block project-plant-data, null when not loaded or denied. The unsaved project
     * has no blocks; the block edits the project itself and its values go with `createProject`.
     */
    plantDataProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.plantData ?? null;
    },
    /**
     * Data of the block project-financing, null when not loaded or denied. The unsaved project
     * has no blocks; the block edits the project itself and its values go with `createProject`.
     */
    financingProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.financing ?? null;
    },
    /**
     * Data of the block project-iab-tax-data, null when not loaded or denied. The unsaved project
     * has no blocks; the block edits the project itself and its values go with `createProject`.
     */
    iabTaxDataProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.iabTaxData ?? null;
    },
  },
};
</script>
